import { DialogTitle, GridProps, useTheme } from '@mui/material';
import { Condition, ContextForm, FileUpload, IConditionProps, IFormAfterSubmit, IFormLookupFilterProps, InputOnlyField, Select, Text } from '@ngt/forms';
import { ConditionMode, ValidateOn } from '@ngt/forms-core';
import { FC, useCallback, useContext } from 'react';
import { Attachment, AttachmentParentTypes, Project } from '../api/dtos';
import LotAttachmentsContext from '../contexts/LotAttachmentsContext';
import { FixedHeightInputOnlyField, FormSection, IFormDialogProps, StyledButton, StyledDialog, StyledDialogActions, StyledDialogContent, StyledFormColumn, StyledFormSubmitButton } from './inputs/CommonFormElements';
import StageAttachmentsContext from '../contexts/StageAttachmentsContext';
import ProjectAttachmentsContext from '../contexts/ProjectAttachmentsContext';

const COLUMN_SIZING: GridProps['size'] = {
    xs: 12,
    sm: 12,
    md: 12,
    lg: 12,
    xl: 12
}

const ParentTypeLookupFilter: IFormLookupFilterProps<Attachment>['filterFunction'] = (_formState, _propertyPath, lookup) => {
    if (!lookup) {
        return undefined;
    }

    return {
        ...lookup,
        items: lookup.items
            ?.filter(x => {
                return x.id !== AttachmentParentTypes.Lot
            })
    }
}

const typeConditionFn: IConditionProps<Attachment>['condition'] = ({ formState }) => {
    return formState?.value?.attachmentParentType !== AttachmentParentTypes.Lot;
}

const onConditionNotMet = () => {};

const LotForm: FC<IFormDialogProps> = (props) => {
    const theme = useTheme();
    const { refresh: refreshLotAttachments } = useContext(LotAttachmentsContext);
    const { refresh: refreshStageAttachments } = useContext(StageAttachmentsContext);
    const { refresh: refreshProjectAttachments } = useContext(ProjectAttachmentsContext);

    const onAfterSubmit: IFormAfterSubmit<Project> = useCallback(async (_newValue, _formState, _formActions) => {
        if(refreshLotAttachments) {
            await refreshLotAttachments();
        }
        if(refreshStageAttachments) {
            await refreshStageAttachments();
        }
        if(refreshProjectAttachments) {
            await refreshProjectAttachments();
        }
        props.onClose();
    }, [refreshStageAttachments, refreshLotAttachments, props.onClose])

    return (
        <ContextForm
            onAfterSubmit={onAfterSubmit}
            validateOn={ValidateOn.onChange}
            validateOnReset
        >
            <StyledDialog {...props}>
                <DialogTitle
                    sx={{
                        backgroundColor: theme.palette.secondary.light,
                        color: "white"
                    }}
                >
                    {props.edit ? "Edit" : "Add"} Attachment
                </DialogTitle>
                <StyledDialogContent>
                    <FormSection>
                        <StyledFormColumn
                            size={COLUMN_SIZING}
                        >
                            <Condition 
                                condition={typeConditionFn}
                                mode={ConditionMode.Show}
                                subscription={{ value: true }}
                                onConditionNotMet={onConditionNotMet}
                            >
                                <InputOnlyField 
                                    label={'Add To'}
                                    component={Select}
                                    propertyPath={'attachmentParentType'}
                                    LookupFilterProps={{
                                        filterFunction: ParentTypeLookupFilter,
                                        subscription: {
                                            initialValue: true
                                        }
                                    }}
                                />
                            </Condition>
                            <InputOnlyField 
                                label={'Name'}
                                component={Text}
                                propertyPath={'name'}
                            />
                            <FixedHeightInputOnlyField 
                                label={'File Name'}
                                component={FileUpload}
                                propertyPath={'file'}
                            />
                            <InputOnlyField
                                label={'Attachment Type'}
                                component={Select}
                                propertyPath={'attachmentTypeId'}
                            />
                        </StyledFormColumn>
                    </FormSection>
                </StyledDialogContent>
                <StyledDialogActions>
                    <StyledFormSubmitButton variant="contained">Submit</StyledFormSubmitButton>
                    <StyledButton variant="contained" onClick={props.onClose}>Cancel</StyledButton>
                </StyledDialogActions>
            </StyledDialog>
        </ContextForm>
    );
}

export default LotForm;